import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO = '/assets/images/SeedDrive_Black_-1772706977187.png';

const navLinks = [
  { label: 'Home', path: '/home-landing-page' },
  { label: 'Platform', path: '/platform-overview-page' },
  { label: 'How it works', path: '/how-it-works-page' },
  { label: 'Security', path: '/security-compliance-page' },
  { label: 'About', path: '/about-page' },
  { label: 'Contact', path: '/contact-page' },
];

export default function LandingNav({ dark = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location?.pathname]);

  // Dark variant: transparent until scroll, then solid dark bg
  const isDark = dark;
  const navBg = isDark
    ? scrolled ? '#1e2b2b' : 'transparent'
    : scrolled ? 'rgba(242,240,236,0.96)' : 'rgba(242,240,236,0.90)';
  const navBorder = isDark
    ? scrolled ? '1px solid rgba(237,232,223,0.12)' : '1px solid transparent'
    : scrolled ? '1px solid #e0e0e0' : '1px solid transparent';
  const linkColor = isDark ? 'rgba(242,240,236,0.65)' : '#6b7b7b';
  const activeLinkColor = isDark ? '#F2F0EC' : '#3b4c4c';
  const activeLinkBg = isDark ? 'rgba(242,240,236,0.10)' : 'rgba(59,76,76,0.08)';
  const mobileBg = isDark ? '#1e2b2b' : '#F2F0EC';
  const mobileBorder = isDark ? 'rgba(237,232,223,0.12)' : '#e0e0e0';
  const mobileButtonColor = isDark ? '#F2F0EC' : '#3b4c4c';

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300"
      style={{
        background: navBg,
        backdropFilter: isDark && !scrolled ? 'none' : 'blur(8px)',
        borderBottom: navBorder,
        boxShadow: scrolled && !isDark ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/home-landing-page" className="flex items-center group">
            <img
              src={LOGO}
              alt="SeedDrive logo"
              style={{
                height: '36px',
                width: 'auto',
                filter: isDark ? 'brightness(0) invert(1)' : 'none',
              }}
              className="object-contain transition-all duration-300"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => {
              const isActive = location?.pathname === link?.path;
              return (
                <Link
                  key={link?.path}
                  to={link?.path}
                  className="px-3 py-2 rounded-md text-sm font-medium font-caption transition-colors duration-200"
                  style={{
                    color: isActive ? activeLinkColor : linkColor,
                    background: isActive ? activeLinkBg : undefined,
                    fontWeight: isActive ? 600 : undefined,
                  }}
                >
                  {link?.label}
                </Link>
              );
            })}
          </nav>

          {/* Log In */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 rounded-md text-sm font-medium font-caption transition-all duration-200 hover:opacity-90"
              style={{
                background: isDark ? '#F2F0EC' : '#3b4c4c',
                color: isDark ? '#1e2b2b' : 'white',
              }}
            >
              Log In
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-md transition-colors duration-200"
            style={{ color: mobileButtonColor }}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 6H17M3 10H17M3 14H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden px-6 py-4 space-y-1 border-t" style={{ background: mobileBg, borderColor: mobileBorder }}>
          {navLinks?.map((link) => {
            const isActive = location?.pathname === link?.path;
            return (
              <Link
                key={link?.path}
                to={link?.path}
                className="block px-3 py-2.5 rounded-md text-sm font-medium font-caption transition-colors"
                style={{
                  color: isActive ? activeLinkColor : linkColor,
                  background: isActive ? activeLinkBg : undefined,
                }}
              >
                {link?.label}
              </Link>
            );
          })}
          <div className="pt-3 border-t" style={{ borderColor: mobileBorder }}>
            <Link
              to="/login"
              className="block w-full text-center px-4 py-2.5 rounded-md text-sm font-medium font-caption"
              style={{ background: isDark ? '#F2F0EC' : '#3b4c4c', color: isDark ? '#1e2b2b' : 'white' }}
            >
              Log In
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
